import type {
  MyRoster,
  MyRosterShift,
  RosterChange,
  RosterPerson,
  RosterPublicationPreview,
} from "../../api/rosterSchemas";
import { rosterDayTargets } from "../../domain/rosterTarget";
import type { RosterTargetPlan } from "../../domain/rosterTarget";
import { parseInstant } from "../../domain/time";
import type { EpochSeconds, LocalDate } from "../../domain/time";

/** Local times of a shift as the API sends them. */
export interface LocalShiftTimes {
  readonly startLocalTime: string;
  readonly endLocalTime: string;
  readonly endsNextDay: boolean;
}

/**
 * Formats the local times of a shift as `06:00–14:00`, with `+1` when it ends on the next day.
 *
 * @param shift - The shift.
 * @returns The times.
 */
export function timeRange(shift: LocalShiftTimes): string {
  const range = `${shift.startLocalTime.slice(0, 5)}–${shift.endLocalTime.slice(0, 5)}${shift.endsNextDay ? " +1" : ""}`;
  return range;
}

/**
 * Returns the next own shift that has not ended yet: the one that runs now or begins next. The
 * clock screen only shows it; it never records anything (`SINV-01`).
 *
 * @param roster - „Mein Dienstplan“.
 * @param now - Now in epoch seconds.
 * @returns The shift, or `null`.
 */
export function nextOwnShift(roster: MyRoster, now: EpochSeconds): MyRosterShift | null {
  const next =
    roster.shifts
      .filter((shift) => shift.kind === "Own" && parseInstant(shift.end) > now)
      .sort((left, right) => parseInstant(left.start) - parseInstant(right.start))[0] ?? null;

  return next;
}

/** The shifts of one day of „Mein Dienstplan“. */
export interface RosterDay {
  readonly date: LocalDate;
  readonly shifts: readonly MyRosterShift[];
}

/**
 * Groups the shifts from a day on by their local day, in the order of the days; days without shifts
 * are left out.
 *
 * @param shifts - The shifts ordered by begin.
 * @param from - The first day shown (today), so a roster kept on the device shows no past days.
 * @returns The days.
 */
export function rosterDays(shifts: readonly MyRosterShift[], from: LocalDate): RosterDay[] {
  const byDate = new Map<LocalDate, MyRosterShift[]>();

  for (const shift of shifts.filter((item) => item.date >= from)) {
    const list = byDate.get(shift.date) ?? [];
    list.push(shift);
    byDate.set(shift.date, list);
  }

  const days = [...byDate.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([date, list]) => ({ date, shifts: list }));

  return days;
}

/**
 * Returns the target from the roster of the days with own shifts on which the own model takes its target from the
 * published roster (T-180), computed like the balance in the backend (`rosterDayTargets`, shared cases), also from the
 * roster kept on the device. Holidays and absences are not known here; the balance shows the final target.
 *
 * @param roster - „Mein Dienstplan“.
 * @returns The planned working time per day in seconds.
 */
export function ownRosterTargets(roster: MyRoster): Map<LocalDate, number> {
  const own = roster.shifts.filter((shift) => shift.kind === "Own");
  const plans: RosterTargetPlan[] = roster.establishments.map((establishment) => ({
    from: roster.from,
    to: roster.to,
    timeZone: establishment.timeZoneId,
    versions: [
      {
        publishedAt: 0,
        shifts: own
          .filter((shift) => shift.establishmentId === establishment.id)
          .map((shift) => ({
            start: parseInstant(shift.start),
            end: parseInstant(shift.end),
            plannedBreakSeconds: shift.plannedBreakMinutes * 60,
          })),
      },
    ],
  }));
  const governed = (date: LocalDate): boolean =>
    roster.targetFromRoster.some((range) => range.from <= date && date <= range.to);
  const owned = new Set(own.map((shift) => shift.date));
  const targets = new Map(
    [...rosterDayTargets(plans, new Map()).entries()].filter(
      ([date]) => governed(date) && owned.has(date),
    ),
  );

  return targets;
}

/** Counts of a change list. */
export interface ChangeCounts {
  readonly added: number;
  readonly changed: number;
  readonly removed: number;
}

/**
 * Counts the changes of a publication by kind.
 *
 * @param changes - The change list.
 * @returns The counts.
 */
export function countChanges(changes: readonly RosterChange[]): ChangeCounts {
  const counts: ChangeCounts = {
    added: changes.filter((change) => change.kind === "Added").length,
    changed: changes.filter((change) => change.kind === "Changed").length,
    removed: changes.filter((change) => change.kind === "Removed").length,
  };

  return counts;
}

/**
 * Orders the change list of a publication by the begin of the shift (after the change, or before
 * it when removed), so the planner reads it day by day; the API orders it by shift (F-57).
 *
 * @param changes - The change list.
 * @returns The changes, earliest first.
 */
export function changesInTimeOrder(changes: readonly RosterChange[]): RosterChange[] {
  const beginOf = (change: RosterChange): EpochSeconds => {
    const shift = change.shift ?? change.previous;
    const begin = shift === null ? 0 : parseInstant(shift.start);

    return begin;
  };
  const ordered = changes.toSorted((first, second) => beginOf(first) - beginOf(second));

  return ordered;
}

/** Who a shift of a change list belongs to. */
export type PersonLabel =
  | { readonly kind: "name"; readonly name: string }
  | { readonly kind: "open" }
  | { readonly kind: "hidden" };

/**
 * Returns the label of the person of a shift: a name, an open shift, or a person outside the
 * planner's view – never shown as an open shift.
 *
 * @param persons - Persons of the planning view with their names.
 * @param employeeId - The person of the shift; `null` for an open shift or a hidden person.
 * @param hidden - Whether the shift belongs to a person outside the planner's view.
 * @returns The label.
 */
export function personLabel(
  persons: readonly RosterPerson[],
  employeeId: string | null,
  hidden: boolean,
): PersonLabel {
  const name =
    employeeId === null
      ? null
      : (persons.find((person) => person.employeeId === employeeId)?.displayName ?? null);
  const label: PersonLabel = hidden
    ? { kind: "hidden" }
    : name === null
      ? employeeId === null
        ? { kind: "open" }
        : { kind: "hidden" }
      : { kind: "name", name };

  return label;
}

/**
 * Returns whose shift a change concerns: the shift after the change, or before it when removed.
 *
 * @param persons - Persons of the planning view with their names.
 * @param change - The change.
 * @returns The label.
 */
export function personOf(persons: readonly RosterPerson[], change: RosterChange): PersonLabel {
  const label =
    change.shift === null
      ? personLabel(persons, change.previous?.employeeId ?? null, change.previousPersonHidden)
      : personLabel(persons, change.shift.employeeId, change.shiftPersonHidden);

  return label;
}

/**
 * Returns whether a changed shift went to another person (or between a person and an open shift),
 * so the change list names the person before as well (F-57).
 *
 * @param change - The change.
 * @returns `true` if the person differs before and after the change.
 */
export function personChanged(change: RosterChange): boolean {
  const changed =
    (change.previous?.employeeId ?? null) !== (change.shift?.employeeId ?? null) ||
    change.previousPersonHidden !== change.shiftPersonHidden;

  return changed;
}

/**
 * Returns the full calendar days between today and the day of a shift, neither counted: the measure
 * of the lead time (`RosterLeadTime` in the domain, ADR-0150 #4). The API sends the difference of
 * the two days, one more (F-57).
 *
 * @param daysAhead - Calendar days from today to the day of the shift.
 * @returns The full days between, never negative.
 */
export function fullDaysBetween(daysAhead: number): number {
  const days = Math.max(daysAhead - 1, 0);

  return days;
}

/**
 * Checks the reason of a publication: required while the plan check shows notices, at most 500
 * characters.
 *
 * @param preview - The preview of the publication.
 * @param reason - The reason entered.
 * @returns The message key of the error, or `null`.
 */
export function reasonError(
  preview: RosterPublicationPreview,
  reason: string,
): "roster.publish.reasonMissing" | "roster.publish.reasonTooLong" | null {
  const trimmed = reason.trim();
  const error =
    preview.reasonRequired && trimmed === ""
      ? "roster.publish.reasonMissing"
      : trimmed.length > 500
        ? "roster.publish.reasonTooLong"
        : null;

  return error;
}
